import React, { useState, Fragment } from 'react';
import { Dialog, Transition, Listbox } from '@headlessui/react';
import {
  TrashIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import { formatExchangeRate } from '../../utils/formatters';

const Settings = () => {
  const { settings, exchangeRates, updateSettings, updateExchangeRates, clearAllData } = useData();
  const [editingRates, setEditingRates] = useState(false);
  const [rateValues, setRateValues] = useState(exchangeRates?.rates || {});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null });

  const currencyOptions = [
    { id: 'GBP', name: 'GBP (British Pound)' },
    { id: 'USD', name: 'USD (US Dollar)' },
    { id: 'EUR', name: 'EUR (Euro)' },
    { id: 'JPY', name: 'JPY (Japanese Yen)' },
  ];

  const themeOptions = [
    { id: 'light', name: 'Light' },
    { id: 'dark', name: 'Dark' },
  ];

  const handleCurrencyChange = (option) => {
    updateSettings({ ...settings, displayCurrency: option.id });
  };

  const handleThemeChange = (option) => {
    updateSettings({ ...settings, theme: option.id });
  };

  const handleClearData = () => {
    clearAllData();
    // Clear the welcome modal dismissed flag so it shows again on next login
    localStorage.removeItem('welcomeModalDismissed');
    setConfirmDialog({ open: false, type: null });
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const handleSaveExchangeRates = () => {
    const newRates = {
      baseCurrency: exchangeRates?.baseCurrency || settings.displayCurrency,
      rates: rateValues,
    };
    updateExchangeRates(newRates);
    setEditingRates(false);
  };

  const handleCancelEditRates = () => {
    setRateValues(exchangeRates?.rates || {});
    setEditingRates(false);
  };

  const handleRateChange = (currency, value) => {
    setRateValues(prev => ({
      ...prev,
      [currency]: parseFloat(value) || 0,
    }));
  };

  const selectedCurrency = currencyOptions.find(o => o.id === settings.displayCurrency) || currencyOptions[0];
  const selectedTheme = themeOptions.find(o => o.id === settings.theme) || themeOptions[0];

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Settings</h2>

      {/* General Settings */}
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-300 mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">General Settings</h3>

        <div className="flex flex-col gap-6">
          {/* Display Currency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Display Currency</label>
            <Listbox value={selectedCurrency} onChange={handleCurrencyChange}>
              <div className="relative w-64">
                <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <span className="block truncate">{selectedCurrency.name}</span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                  </span>
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {currencyOptions.map((option) => (
                      <Listbox.Option
                        key={option.id}
                        value={option}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                            active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {option.name}
                            </span>
                            {selected && (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                <CheckIcon className="h-5 w-5" />
                              </span>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
            <p className="mt-2 text-xs text-gray-600">
              All portfolio values will be converted to this currency for display
            </p>
          </div>

          {/* Theme */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
            <Listbox value={selectedTheme} onChange={handleThemeChange}>
              <div className="relative w-64">
                <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <span className="block truncate">{selectedTheme.name}</span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                  </span>
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {themeOptions.map((option) => (
                      <Listbox.Option
                        key={option.id}
                        value={option}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                            active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {option.name}
                            </span>
                            {selected && (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                <CheckIcon className="h-5 w-5" />
                              </span>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
            <p className="mt-2 text-xs text-gray-600">
              Choose your preferred color theme
            </p>
          </div>
        </div>
      </div>

      {/* Exchange Rates */}
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-300 mb-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Exchange Rates</h3>
          {!editingRates && (
            <button
              onClick={() => setEditingRates(true)}
              className="flex items-center gap-2 px-4 py-2  text-white rounded-lg font-medium  transition-colors"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
            >
              <PencilIcon className="w-4 h-4" />
              <span>Edit Rates</span>
            </button>
          )}
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-700">
            Base Currency: <span className="font-semibold">{exchangeRates?.baseCurrency || settings.displayCurrency}</span>
          </p>
          <p className="text-sm text-gray-700">
            Last Updated: <span className="font-semibold">{exchangeRates?.lastUpdated || 'Never'}</span>
          </p>
        </div>

        {editingRates ? (
          <div className="flex flex-col gap-4">
            {Object.keys(exchangeRates?.rates || {}).map(currency => (
              <div key={currency}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {currency} Exchange Rate
                </label>
                <input
                  type="number"
                  value={rateValues[currency]}
                  onChange={(e) => handleRateChange(currency, e.target.value)}
                  disabled={currency === exchangeRates?.baseCurrency}
                  step="0.0001"
                  min="0"
                  className="block w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-600">
                  1 {exchangeRates?.baseCurrency} = X {currency}
                </p>
              </div>
            ))}
            <div className="flex gap-2">
              <button
                onClick={handleSaveExchangeRates}
                className="px-4 py-2  text-white rounded-lg font-medium  transition-colors"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
              >
                Save Rates
              </button>
              <button
                onClick={handleCancelEditRates}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <table className="w-full border-collapse">
            <thead className="bg-white border-b border-gray-200">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Currency</th>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Exchange Rate</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {Object.entries(exchangeRates?.rates || {}).map(([currency, rate]) => (
                <tr key={currency} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-900">{currency}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">
                    1 {exchangeRates?.baseCurrency} = {formatExchangeRate(rate)} {currency}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Data Management */}
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-300 mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Management</h3>

        <div className="flex flex-col gap-6">
          {/* Clear All Data */}
          <div>
            <h4 className="text-base font-semibold text-gray-900 mb-1">Clear All Data</h4>
            <p className="text-xs text-gray-600 mb-2">
              Permanently delete all portfolio data from Firebase. This action cannot be undone.
            </p>
            <p className="text-xs text-yellow-600 font-semibold mb-3">
              ⚠️ Warning: This will delete all your portfolio data, goals, transactions, and settings.
            </p>
            <button
              onClick={() => setConfirmDialog({ open: true, type: 'clear' })}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              <span>Clear All Data</span>
            </button>
          </div>
        </div>
      </div>

      {/* About */}
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-300">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">About</h3>
        <div className="flex flex-col gap-2">
          <div>
            <p className="font-semibold text-gray-900">Portfolio Goal Tracker</p>
            <p className="text-xs text-gray-600">Version 1.0.0</p>
          </div>
          <p className="text-xs text-gray-600">
            Track investment goals across multiple portfolio accounts. All data is securely stored in Firebase.
          </p>
          <p className="text-xs text-gray-600">
            Built with React, Tailwind CSS, Headless UI, and Recharts.
          </p>
        </div>
      </div>

      {/* Confirmation Dialogs */}
      <Transition appear show={confirmDialog.open} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setConfirmDialog({ open: false, type: null })}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                      Delete all data?
                    </Dialog.Title>
                  </div>

                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      This action cannot be undone. Are you sure?
                    </p>
                  </div>

                  <div className="mt-6 flex gap-3 justify-end">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      onClick={() => setConfirmDialog({ open: false, type: null })}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-white rounded-lg bg-red-600 hover:bg-red-700 transition-colors"
                      onClick={handleClearData}
                    >
                      Yes, delete everything
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default Settings;
